import React from 'react';
import { Form, Button, Card, ListGroup, Row, Col } from 'react-bootstrap';

const MaterialsSection = ({ 
  materials, 
  currentMaterial,
  categories,
  vendors,
  onMaterialChange, 
  onAddMaterial, 
  onRemoveMaterial 
}) => {
  
  // Calculate total materials cost
  const totalMaterialsCost = materials.reduce((sum, m) => sum + (parseFloat(m.totalCost) || 0), 0);
  
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Materials</h4>
        {materials.length > 0 && (
          <div className="text-end">
            <small className="text-muted d-block">Total Materials Cost</small>
            <h5 className="mb-0 text-success">${totalMaterialsCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h5>
          </div>
        )}
      </div>
      
      {/* Materials List */}
      {materials.length > 0 && (
        <div className="mb-4">
          <ListGroup>
            {materials.map((material, index) => (
              <ListGroup.Item key={material.id} className="d-flex justify-content-between align-items-start">
                <div className="ms-2 me-auto">
                  <div className="d-flex align-items-center gap-2">
                    <div className="fw-bold">{material.materialName}</div>
                    {material.category && (
                      <span className="badge bg-secondary">{material.category}</span>
                    )}
                  </div>
                  <div className="text-muted small mt-1">
                    {material.quantity} {material.unit} × ${parseFloat(material.unitCost).toFixed(2)} = ${parseFloat(material.totalCost).toFixed(2)}
                  </div>
                  {material.vendor && (
                    <div className="text-muted small">Vendor: {material.vendor}</div>
                  )}
                  {material.notes && (
                    <div className="text-muted small mt-1">{material.notes}</div>
                  )}
                </div>
                <Button 
                  variant="outline-danger" 
                  size="sm"
                  onClick={() => onRemoveMaterial(material.id)}
                >
                  Remove
                </Button>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </div>
      )}
      
      {/* Add Material Form */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Material Name <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  name="materialName"
                  value={currentMaterial.materialName}
                  onChange={onMaterialChange}
                  placeholder="e.g., Ceramic Tile, Bathtub, Paint"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Category</Form.Label>
                <Form.Control
                  as="select"
                  name="category"
                  value={currentMaterial.category}
                  onChange={onMaterialChange}
                >
                  <option value="">Select or type new...</option>
                  <option value="Plumbing">Plumbing</option>
                  <option value="Electrical">Electrical</option>
                  <option value="Flooring">Flooring</option>
                  <option value="Fixtures">Fixtures</option>
                  <option value="Lumber">Lumber</option>
                  <option value="Paint">Paint</option>
                  <option value="Hardware">Hardware</option>
                  <option value="HVAC">HVAC</option>
                  <option value="Roofing">Roofing</option>
                  <option value="Other">Other</option>
                  {categories.filter(c => 
                    !['Plumbing', 'Electrical', 'Flooring', 'Fixtures', 'Lumber', 'Paint', 'Hardware', 'HVAC', 'Roofing', 'Other'].includes(c)
                  ).map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </Form.Control>
                <Form.Text className="text-muted">
                  Or type a new category below
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
          
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Quantity <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  name="quantity"
                  value={currentMaterial.quantity}
                  onChange={onMaterialChange}
                  placeholder="e.g., 150"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Unit</Form.Label>
                <Form.Control
                  as="select"
                  name="unit"
                  value={currentMaterial.unit}
                  onChange={onMaterialChange}
                >
                  <option value="each">Each</option>
                  <option value="sq ft">Square Feet</option>
                  <option value="linear ft">Linear Feet</option>
                  <option value="gallon">Gallon</option>
                  <option value="box">Box</option>
                  <option value="bag">Bag</option>
                  <option value="sheet">Sheet</option>
                  <option value="roll">Roll</option>
                  <option value="set">Set</option>
                </Form.Control>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Unit Cost ($) <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  name="unitCost"
                  value={currentMaterial.unitCost}
                  onChange={onMaterialChange}
                  placeholder="e.g., 4.50"
                />
              </Form.Group>
            </Col>
          </Row>
          
          {currentMaterial.quantity && currentMaterial.unitCost && (
            <div className="alert alert-info mb-3">
              <strong>Total Cost:</strong> ${(parseFloat(currentMaterial.quantity || 0) * parseFloat(currentMaterial.unitCost || 0)).toFixed(2)}
            </div>
          )}
          
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Vendor</Form.Label>
                <Form.Control
                  type="text"
                  name="vendor"
                  value={currentMaterial.vendor}
                  onChange={onMaterialChange}
                  placeholder="e.g., Home Depot, Lowe's"
                  list="vendor-list"
                />
                <datalist id="vendor-list">
                  {vendors.map((vendor, idx) => (
                    <option key={idx} value={vendor} />
                  ))}
                </datalist>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Purchase Date</Form.Label>
                <Form.Control
                  type="date"
                  name="purchaseDate"
                  value={currentMaterial.purchaseDate}
                  onChange={onMaterialChange}
                />
              </Form.Group>
            </Col>
          </Row>
          
          <Form.Group className="mb-3">
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              name="notes"
              value={currentMaterial.notes}
              onChange={onMaterialChange}
              placeholder="Any additional notes about this material..."
            />
          </Form.Group>
          
          <div className="d-flex justify-content-end">
            <Button 
              variant="success" 
              onClick={onAddMaterial}
              disabled={!currentMaterial.materialName || !currentMaterial.quantity || !currentMaterial.unitCost}
            >
              Add Material
            </Button>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default MaterialsSection;
